import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'

// Strip line and block comments from JSON, respecting quoted strings.
function stripJsonComments(input: string): string {
  let result = ''
  let i = 0
  while (i < input.length) {
    // Inside a string — copy until closing quote
    if (input[i] === '"') {
      let j = i + 1
      while (j < input.length) {
        if (input[j] === '\\') {
          j += 2
          continue
        }
        if (input[j] === '"') {
          j++
          break
        }
        j++
      }
      result += input.slice(i, j)
      i = j
      // Line comment
    } else if (input[i] === '/' && input[i + 1] === '/') {
      const nl = input.indexOf('\n', i)
      i = nl === -1 ? input.length : nl
      // Block comment
    } else if (input[i] === '/' && input[i + 1] === '*') {
      const end = input.indexOf('*/', i + 2)
      i = end === -1 ? input.length : end + 2
    } else {
      result += input[i]
      i++
    }
  }
  return result
}

function createAdminPathAliases(config: ResolvedBetterstartConfig): Record<string, string[]> {
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const adminPath = config.paths.admin

  return {
    [`${namespace.alias}/*`]: [`${adminPath}/*`],
    [`${namespace.alias}/db`]: [`${adminPath}/lib/db/client`],
    [`${namespace.alias}/db/schema`]: [`${adminPath}/lib/db/schema`],
    [`${namespace.alias}/actions/*`]: [`${adminPath}/lib/actions/*`],
    [`${namespace.alias}/auth/server`]: [`${adminPath}/lib/actions/auth/server`],
    [`${namespace.alias}/auth/client`]: [`${adminPath}/lib/actions/auth/client`],
    [`${namespace.alias}/auth/session`]: [`${adminPath}/lib/actions/auth/session`],
    [`${namespace.alias}/hooks/*`]: [`${adminPath}/hooks/*`],
    [`${namespace.alias}/components/*`]: [`${adminPath}/components/*`],
    [`${namespace.alias}/types`]: [`${adminPath}/types`],
    [`${namespace.alias}/types/*`]: [`${adminPath}/types/*`],
    [`${namespace.alias}/utils/*`]: [`${adminPath}/utils/*`]
  }
}

const DEPRECATED_ADMIN_PATH_ALIAS_SUFFIXES = ['/data/*', '/auth', '/auth/middleware']

function sameTargets(a: string[] | undefined, b: string[]): boolean {
  return Boolean(a && a.length === b.length && a.every((value, index) => value === b[index]))
}

/**
 * Add or update BetterStart-managed admin path aliases in tsconfig.json.
 */
export function scaffoldTsconfig(
  cwd: string,
  config: ResolvedBetterstartConfig
): { added: string[]; skipped: string[] } {
  const tsconfigPath = path.join(cwd, 'tsconfig.json')
  const added: string[] = []
  const skipped: string[] = []

  if (!fs.existsSync(tsconfigPath)) {
    skipped.push('tsconfig.json not found')
    return { added, skipped }
  }

  const raw = fs.readFileSync(tsconfigPath, 'utf-8')

  // Strip JSON comments while preserving string contents.
  // Walks character-by-character to avoid mangling strings that contain /* or //.
  const stripped = stripJsonComments(raw)
    // Remove trailing commas before } or ]
    .replace(/,\s*([\]}])/g, '$1')

  let tsconfig: Record<string, unknown>
  try {
    tsconfig = JSON.parse(stripped)
  } catch {
    skipped.push('Failed to parse tsconfig.json')
    return { added, skipped }
  }

  const compilerOptions = (tsconfig.compilerOptions ?? {}) as Record<string, unknown>
  const paths = (compilerOptions.paths ?? {}) as Record<string, string[]>

  if (compilerOptions.resolveJsonModule === true) {
    skipped.push('compilerOptions.resolveJsonModule')
  } else {
    compilerOptions.resolveJsonModule = true
    added.push('compilerOptions.resolveJsonModule')
  }

  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  const aliases = createAdminPathAliases(config)
  for (const [alias, target] of Object.entries(aliases)) {
    if (sameTargets(paths[alias], target)) {
      skipped.push(alias)
    } else {
      paths[alias] = target
      added.push(alias)
    }
  }

  for (const alias of Object.keys(paths)) {
    const isDefaultAdminAliasForCustomNamespace =
      namespace.alias !== '@admin' && (alias === '@admin' || alias.startsWith('@admin/'))
    const isDeprecatedCurrentAlias = DEPRECATED_ADMIN_PATH_ALIAS_SUFFIXES.some(
      (suffix) => alias === `${namespace.alias}${suffix}`
    )

    if (isDefaultAdminAliasForCustomNamespace || isDeprecatedCurrentAlias) {
      delete paths[alias]
      added.push(alias)
    }
  }

  compilerOptions.paths = paths
  tsconfig.compilerOptions = compilerOptions

  fs.writeFileSync(tsconfigPath, `${JSON.stringify(tsconfig, null, 2)}\n`, 'utf-8')

  return { added, skipped }
}
